/**
 * Organization settings — which today is identity and the danger zone.
 *
 * The screen exists for the second one. Every other destructive action in the
 * dashboard removes a row someone can recreate in a minute; this one takes an
 * entire audience, sending history and consent record, and there is no undo
 * once the purge runs. So it gets its own place, at the bottom of a screen
 * nobody visits by accident, rather than a menu item next to "Refresh".
 *
 * Deleting does not delete. It schedules: the organization stops
 * authenticating immediately and the data survives for a week, which is what
 * makes a misclick recoverable at all.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { CustomFields } from "./CustomFields";
import { Usage } from "./Usage";
import { Icon } from "../components/Sprites";
import { grantableRoles, roleInfo, roleLabel, type RoleInfo } from "../roles";
import { useSession } from "../session";
import type {
  AuditEvent,
  CreatedInvitation,
  CursorPage,
  Invitation,
  Member,
  Organization,
  Role,
} from "../api/types";

/**
 * Tabs rather than one long scroll.
 *
 * The danger zone is the reason. On a single page it would sit below the member
 * list, which is the screen an admin visits most — so the most destructive
 * control in the product would be one scroll away from routine work. Tabs put
 * it behind a deliberate click.
 */
type Tab = "organization" | "usage" | "members" | "invitations" | "fields" | "audit";

export function Settings() {
  const { orgId, org, role, can } = useSession();
  const [tab, setTab] = useState<Tab>("organization");

  const { data, error, loading, reload } = useAsync<Organization>(
    (signal) => api.get<Organization>("/v1/organization", { signal }),
    [orgId],
  );

  // Members and invitations are one capability, so they appear and disappear
  // together. A developer sees neither rather than two tabs that 403 — the
  // panel behind each would be an error, not a screen.
  const managesMembers = can("members:manage");

  const tabs: { id: Tab; label: string }[] = [
    { id: "organization", label: "Organization" },
    // Every role holds usage:read, which makes this the tab that gives a
    // developer, marketer or support user a tab strip at all. Deliberately: the
    // send limit refuses whoever sends campaigns, and they need the figure
    // before composing a send rather than from the refusal after it.
    ...(can("usage:read") ? ([{ id: "usage", label: "Usage" }] as { id: Tab; label: string }[]) : []),
    ...(managesMembers
      ? ([
          { id: "members", label: "Members" },
          { id: "invitations", label: "Invitations" },
        ] as { id: Tab; label: string }[])
      : []),
    // A field definition is organization schema rather than audience data, and
    // custom_fields:manage is an admin capability while Contacts is open to
    // marketing and support — which is why this lives here and not there.
    ...(can("custom_fields:manage")
      ? ([{ id: "fields", label: "Custom fields" }] as { id: Tab; label: string }[])
      : []),
    // Separate capability from the two above: an auditor may need to read the
    // trail without being able to change who is in the organization.
    ...(can("audit:read") ? ([{ id: "audit", label: "Audit" }] as { id: Tab; label: string }[]) : []),
  ];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Settings</h1>
      </div>

      <div className="content" style={{ flexDirection: "column", gap: 16, overflowY: "auto" }}>
        {tabs.length > 1 && (
          <div className="tabs" role="tablist" aria-label="Settings sections">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                // aria-selected is what the active style keys off, so the
                // styling and the semantics are the same edit.
                aria-selected={tab === t.id}
                className="tabs__tab"
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tab === "organization" && (
          <>
            <div className="oc-panel oc-starfield" style={{ padding: 20 }}>
              <h2 className="oc-headline-md" style={{ marginTop: 0 }}>
                Organization
              </h2>

              {loading && <div className="oc-body-sm oc-text-muted-aa">Loading…</div>}
              {error && (
                <div className="field-error" role="alert">
                  {error.message}
                </div>
              )}

              {data && (
                <dl className="settings__facts">
                  <dt className="oc-label">Name</dt>
                  <dd className="oc-body-sm">{data.name}</dd>

                  <dt className="oc-label">Slug</dt>
                  <dd className="oc-mono-sm">{data.slug}</dd>

                  <dt className="oc-label">Organization ID</dt>
                  {/* The value someone puts in X-OpenComms-Org, so it is
                      selectable rather than decorative. */}
                  <dd className="oc-mono-sm oc-truncate">{data.id}</dd>

                  <dt className="oc-label">Your role</dt>
                  <dd className="oc-body-sm">{role ? roleLabel(role) : "—"}</dd>
                </dl>
              )}
            </div>

            {data && <PostalAddress organization={data} onSaved={reload} />}

            {role && <YourAccess role={role} />}

            <DangerZone name={org?.name ?? data?.name ?? ""} />
          </>
        )}

        {tab === "usage" && can("usage:read") && <Usage />}
        {tab === "members" && managesMembers && <Members />}
        {tab === "invitations" && managesMembers && <Invitations />}
        {tab === "fields" && can("custom_fields:manage") && <CustomFields />}
        {tab === "audit" && can("audit:read") && <AuditTrail />}
      </div>
    </>
  );
}

/**
 * The postal address printed at the foot of every campaign.
 *
 * Shown to everybody, because a campaign is refused while it is empty and the
 * person reading that refusal is often not the one who can set it. Editable
 * with org:manage, the same capability the API checks.
 */
function PostalAddress({ organization, onSaved }: { organization: Organization; onSaved: () => void }) {
  const { can } = useSession();
  const editable = can("org:manage");

  const [value, setValue] = useState(organization.postal_address);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The server tidies what was typed, so the stored form replaces it once the
  // organization is read back. Otherwise the field would still hold the
  // untidied text and read as an unsaved change.
  useEffect(() => setValue(organization.postal_address), [organization.postal_address]);

  const changed = value.trim() !== organization.postal_address;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.patch<Organization>("/v1/organization", { postal_address: value });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the postal address.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oc-panel" style={{ padding: 20 }}>
      <h2 className="oc-headline-md" style={{ marginTop: 0 }}>
        Postal address
      </h2>
      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
        Printed with the organization's name at the foot of every campaign. US law requires a postal
        address in every commercial email, and most other countries require the sender to be
        identifiable. Campaigns cannot be scheduled while it is empty.
      </p>

      {editable ? (
        <form className="stack" style={{ gap: "var(--oc-space-2)" }} onSubmit={submit}>
          <label className="oc-label" htmlFor="postal-address">
            Address
          </label>
          <textarea
            id="postal-address"
            className="oc-input"
            rows={4}
            maxLength={300}
            placeholder={"Street and number\nPostcode and city\nCountry"}
            value={value}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "postal-address-error" : undefined}
            onChange={(event) => {
              setValue(event.target.value);
              setSaved(false);
            }}
            style={{ height: "auto", minHeight: 96, resize: "vertical" }}
          />
          {error && (
            <p id="postal-address-error" className="field-error" role="alert">
              {error}
            </p>
          )}
          <div className="row" style={{ gap: "var(--oc-space-2)", alignItems: "center" }}>
            <button type="submit" className="oc-btn oc-btn--sm oc-btn--primary" disabled={busy || !changed}>
              {busy ? "Saving…" : "Save address"}
            </button>
            {saved && !changed && <span className="oc-body-sm oc-text-secondary">Saved.</span>}
          </div>
        </form>
      ) : organization.postal_address ? (
        <p className="oc-body-sm" style={{ whiteSpace: "pre-line", margin: 0 }}>
          {organization.postal_address}
        </p>
      ) : (
        <p className="oc-body-sm oc-text-warning" style={{ margin: 0 }}>
          Not set. Ask an owner or admin to add it before the next campaign.
        </p>
      )}
    </div>
  );
}

/**
 * What the signed-in person's own role allows.
 *
 * Shown to everybody, not just administrators. Somebody who cannot find a
 * button needs to know whether it is missing or whether they are not allowed to
 * see it, and "ask an admin for a role that includes X" is only actionable if
 * they can name X.
 */
function YourAccess({ role }: { role: Role }) {
  const info = roleInfo(role);
  if (info.can.length === 0 && info.cannot.length === 0) return null;

  return (
    <div className="oc-panel" style={{ padding: 20 }}>
      <h2 className="oc-headline-md" style={{ marginTop: 0 }}>
        Your access
      </h2>
      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
        You are {info.label.toLowerCase()} here. {info.summary}
      </p>
      <RoleDetail info={info} />
    </div>
  );
}

/** The can/cannot pair, shared by the access panel and the role picker. */
function RoleDetail({ info }: { info: RoleInfo }) {
  return (
    <div className="role-detail">
      {info.can.length > 0 && (
        <div>
          <div className="oc-label">Can</div>
          <ul className="role-detail__list">
            {info.can.map((item) => (
              <li key={item} className="oc-body-sm">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {info.cannot.length > 0 && (
        <div>
          <div className="oc-label">Cannot</div>
          <ul className="role-detail__list role-detail__list--no">
            {info.cannot.map((item) => (
              <li key={item} className="oc-body-sm oc-text-secondary">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Owners only, and it says so to everyone else rather than hiding.
 *
 * An admin who cannot find the delete button files a support ticket; an admin
 * who is told the action belongs to an owner knows who to ask. The server
 * enforces it either way — this is the explanation, not the guard.
 */
function DangerZone({ name }: { name: string }) {
  const { orgId, role, reload, session, switchOrg } = useSession();
  const navigate = useNavigate();

  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (role !== "owner") {
    return (
      <div className="oc-panel" style={{ padding: 20 }}>
        <h2 className="oc-headline-md" style={{ marginTop: 0 }}>
          Delete this organization
        </h2>
        {/* Deliberately says nothing about what the reader *can* do.
            The earlier wording — "you can manage everything inside this one" —
            was written when an admin was the only non-owner who reached this
            screen, and reads as false to a developer or an analyst. The panel
            above already states their access precisely. */}
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          Only an owner can delete an organization. Ask one if this needs to happen.
        </p>
      </div>
    );
  }

  // Typing the name, not a checkbox. The point is to make it impossible to do
  // this to the wrong organization while meaning to do it to another — which
  // is the actual failure, not deleting one by accident with no intent at all.
  const armed = confirm.trim() === name.trim() && name !== "";

  const remove = async () => {
    if (!orgId || !armed) return;
    setBusy(true);
    setError(null);

    try {
      await api.delete(`/auth/organizations/${orgId}`, { unscoped: true });

      // Move to another organization before reloading, so the dashboard does
      // not land on the deleted one and answer 401 to its own first request.
      const other = session?.organizations.find((o) => o.id !== orgId && !o.purge_after);
      if (other) {
        switchOrg(other.id);
        navigate("/messages");
      }
      await reload();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not reach the server. Check that it is running.",
      );
      setBusy(false);
    }
  };

  return (
    <div className="oc-panel settings__danger" style={{ padding: 20 }}>
      <h2 className="oc-headline-md" style={{ marginTop: 0, color: "var(--oc-danger)" }}>
        Delete this organization
      </h2>

      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
        Everything goes: contacts and their consent history, message logs, templates, campaigns,
        domains, API keys and webhook endpoints. Sending stops immediately and every API key for
        this organization stops working the moment you confirm.
      </p>
      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
        <strong>You have seven days to change your mind.</strong> Until then the data is intact and
        an owner can restore it from the organization switcher. After that it is deleted for good,
        including from the analytics store and object storage — there is no backup to ask for.
      </p>

      <div className="login__field" style={{ maxWidth: 420 }}>
        {/* The name sits outside the label, not inside it. `.oc-label` is
            uppercased, so an organization called "Acme Corp" rendered there
            reads as ACME CORP — and someone typing exactly what they were
            shown would find the button stayed disabled with no explanation. */}
        <label className="oc-label" htmlFor="confirm-name">
          Confirm by typing the organization name
        </label>
        <div className="oc-mono-sm" style={{ color: "var(--oc-text-primary)" }}>
          {name}
        </div>
        <input
          id="confirm-name"
          className="oc-input"
          autoComplete="off"
          spellCheck={false}
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "confirm-error" : undefined}
        />
      </div>

      {error && (
        <div className="field-error" id="confirm-error" role="alert">
          {error}
        </div>
      )}

      <button
        type="button"
        className="oc-btn oc-btn--danger"
        disabled={!armed || busy}
        onClick={() => void remove()}
      >
        <Icon name="warning" size={14} />
        {busy ? "Scheduling…" : "Schedule deletion"}
      </button>
    </div>
  );
}

/**
 * Who is in the organization.
 *
 * Read-only for now: changing somebody's role and removing them both need
 * endpoints that do not exist yet, and a control that cannot work is worse than
 * one that is absent. The list is still the thing an admin needs first — "who
 * has access to this" had no answer at all before.
 */
function Members() {
  const { orgId } = useSession();

  const { data, error, loading, reload } = useAsync<CursorPage<Member>>(
    (signal) => api.get<CursorPage<Member>>("/v1/organization/members", { signal, query: { limit: 100 } }),
    [orgId],
  );

  const rows = data?.data ?? [];

  // Counted here rather than per row, so every row agrees about whether the
  // organization has a spare owner. The server re-checks inside the write.
  const owners = rows.filter((m) => m.role === "owner").length;

  return (
    <div className="oc-panel oc-starfield" style={{ padding: 20 }}>
      <h2 className="oc-headline-md" style={{ marginTop: 0 }}>
        Members
      </h2>

      {loading && <SkeletonRows rows={3} columns={3} />}
      {error && (
        <div className="field-error" role="alert">
          {error.message}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="oc-empty">
          <div className="oc-empty__title">Nobody else yet</div>
          <div className="oc-empty__hint">Invite someone from the Invitations tab.</div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="settings__scroll">
          <table className="settings__table">
            <thead>
              <tr>
                <th className="oc-label">Person</th>
                <th className="oc-label">Role</th>
                <th className="oc-label">Joined</th>
                <th className="oc-label" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <MemberRow key={m.user_id} member={m} owners={owners} onChanged={reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * One member, with the two things an administrator does to them.
 *
 * The last owner's controls are disabled rather than hidden, with the reason
 * attached: an admin who cannot find the remove button for one particular
 * person will assume the screen is broken, whereas "the last owner cannot be
 * removed" tells them what to do instead.
 */
function MemberRow({
  member,
  owners,
  onChanged,
}: {
  member: Member;
  owners: number;
  onChanged: () => void;
}) {
  const { session, role: myRole, can } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = member.user_id === session?.user.id;
  const isLastOwner = member.role === "owner" && owners <= 1;
  const options = grantableRoles(myRole);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setBusy(false);
    }
  };

  // An owner changes their own role through transfer, which promotes the
  // successor and demotes them in one step. Offering a plain dropdown here
  // would let them create the gap transfer exists to avoid.
  const roleLocked = isLastOwner || (isSelf && myRole === "owner");

  return (
    <tr>
      <td>
        <div className="oc-body-sm">
          {member.name || member.email}
          {isSelf && <span className="oc-text-muted-aa"> · you</span>}
        </div>
        {member.name && <div className="oc-mono-sm oc-text-muted-aa">{member.email}</div>}
        {error && (
          <div className="field-error" role="alert">
            {error}
          </div>
        )}
      </td>
      <td>
        {roleLocked || options.length === 0 ? (
          <span className="oc-body-sm">{member.role ? roleLabel(member.role) : "—"}</span>
        ) : (
          <select
            className="oc-input oc-input--sm"
            aria-label={`Role for ${member.email}`}
            value={member.role ?? ""}
            disabled={busy}
            onChange={(event) =>
              void run(() =>
                api.patch(`/v1/organization/members/${member.user_id}`, {
                  role: event.target.value,
                }),
              )
            }
          >
            {options.map((info) => (
              <option key={info.role} value={info.role}>
                {info.label}
              </option>
            ))}
          </select>
        )}
        {isLastOwner && (
          <div className="oc-body-sm oc-text-muted-aa">
            The last owner. Transfer ownership first.
          </div>
        )}
        {isSelf && myRole === "owner" && !isLastOwner && (
          <div className="oc-body-sm oc-text-muted-aa">Use transfer to step down.</div>
        )}
      </td>
      <td className="oc-mono-sm oc-text-muted-aa">
        {new Date(member.created_at).toLocaleDateString()}
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {can("org:transfer") && !isSelf && member.role !== "owner" && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--xs"
            disabled={busy}
            onClick={() => {
              if (
                !window.confirm(
                  `Make ${member.email} the owner of this organization?\n\n` +
                    `They gain full control, including deleting it. You become an admin.`,
                )
              ) {
                return;
              }
              void run(() =>
                api.post("/v1/organization/transfer", { user_id: member.user_id }),
              );
            }}
          >
            Make owner
          </button>
        )}
        {!isLastOwner && !isSelf && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--xs"
            disabled={busy}
            onClick={() => {
              if (!window.confirm(`Remove ${member.email} from this organization?`)) return;
              void run(() => api.delete(`/v1/organization/members/${member.user_id}`));
            }}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Who did what.
 *
 * Read-only by construction — there is no write endpoint, and the table refuses
 * updates and deletes at the database level. What the screen owes the reader is
 * the actor, because "a role was changed" without a name is not an answer to the
 * question anybody opens this tab to ask.
 */
function AuditTrail() {
  const { orgId } = useSession();
  const [action, setAction] = useState("");

  const { data, error, loading, reload } = useAsync<AuditEvent[]>(
    (signal) =>
      api.get<AuditEvent[]>("/v1/audit-events", {
        signal,
        query: { limit: 100, ...(action ? { action } : {}) },
      }),
    [orgId, action],
  );

  const rows = data ?? [];

  // Built from what is on screen rather than from a fixed list: the action set
  // grows with the product, and a hard-coded filter would quietly stop offering
  // the newest thing anybody wants to look for.
  const actions = [...new Set((data ?? []).map((e) => e.action))].sort();

  return (
    <div className="oc-panel oc-starfield" style={{ padding: 20 }}>
      <div className="row row--between">
        <h2 className="oc-headline-md" style={{ margin: 0 }}>
          Audit
        </h2>
        <div className="row">
          <select
            className="oc-input oc-input--sm"
            aria-label="Filter by action"
            value={action}
            onChange={(event) => setAction(event.target.value)}
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      </div>

      <p className="oc-body-sm oc-text-muted-aa" style={{ marginBottom: 0 }}>
        Actions that changed access or how this organization sends, destroyed data, or
        scheduled a campaign. Reads are deliberately not recorded. Entries cannot be edited
        or removed, so a gap is a gap.
      </p>

      {loading && <SkeletonRows rows={4} columns={4} />}
      {error && (
        <div className="field-error" role="alert">
          {error.message}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="oc-empty">
          <div className="oc-empty__title">Nothing recorded yet</div>
          <div className="oc-empty__hint">
            Inviting somebody, changing a role or deleting a domain will appear here.
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="settings__scroll">
          <table className="settings__table">
            <thead>
              <tr>
                <th className="oc-label">When</th>
                <th className="oc-label">Who</th>
                <th className="oc-label">Action</th>
                <th className="oc-label">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="oc-mono-sm oc-text-muted-aa" style={{ whiteSpace: "nowrap" }}>
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="oc-body-sm">{e.actor_label || "—"}</td>
                  <td className="oc-mono-sm">{e.action}</td>
                  <td className="oc-body-sm">
                    {e.target_label || e.target_id}
                    {e.metadata && Object.keys(e.metadata).length > 0 && (
                      <div className="oc-mono-sm oc-text-muted-aa">
                        {Object.entries(e.metadata)
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join(" · ")}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Outstanding and past invitations.
 *
 * The link is the product here, not the email. Creating an invitation returns a
 * URL that works whether or not this deployment can send mail — which is the
 * ordinary state of a fresh install — so the copy button is the primary
 * control and "we emailed it too" is the secondary note.
 */
function Invitations() {
  const { orgId, role } = useSession();
  const [created, setCreated] = useState<CreatedInvitation | null>(null);

  const { data, error, loading, reload } = useAsync<Invitation[]>(
    (signal) => api.get<Invitation[]>("/v1/organization/invitations", { signal }),
    [orgId],
  );

  const rows = data ?? [];

  return (
    <>
      <InviteForm
        granterRole={role}
        onCreated={(inv) => {
          setCreated(inv);
          void reload();
        }}
      />

      {created && <InviteLink invitation={created} onDismiss={() => setCreated(null)} />}

      <div className="oc-panel oc-starfield" style={{ padding: 20 }}>
        <div className="row row--between">
          <h2 className="oc-headline-md" style={{ margin: 0 }}>
            Invitations
          </h2>
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>

        {loading && <SkeletonRows rows={3} columns={4} />}
        {error && (
          <div className="field-error" role="alert">
            {error.message}
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="oc-empty">
            <div className="oc-empty__title">No invitations yet</div>
            <div className="oc-empty__hint">
              Invite someone above. You will get a link to send them, and this deployment will
              email it too if sending is configured.
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <div className="settings__scroll">
            <table className="settings__table">
              <thead>
                <tr>
                  <th className="oc-label">Address</th>
                  <th className="oc-label">Role</th>
                  <th className="oc-label">Status</th>
                  <th className="oc-label" />
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <InvitationRow key={inv.id} invitation={inv} onChanged={reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function InvitationRow({
  invitation,
  onChanged,
}: {
  invitation: Invitation;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/organization/invitations/${invitation.id}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="oc-body-sm">{invitation.email}</td>
      <td className="oc-body-sm">{roleLabel(invitation.role)}</td>
      <td>
        <span className={`invite-status invite-status--${invitation.status}`}>
          {invitation.status}
        </span>
        {invitation.status === "pending" && (
          <div className="oc-mono-sm oc-text-muted-aa">
            expires {new Date(invitation.expires_at).toLocaleDateString()}
          </div>
        )}
        {error && (
          <div className="field-error" role="alert">
            {error}
          </div>
        )}
      </td>
      <td style={{ textAlign: "right" }}>
        {/* Only a pending invitation can be revoked. An accepted one is a
            membership now — taking it back is removing a member, which is a
            different action against a different endpoint. */}
        {invitation.status === "pending" && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--xs"
            disabled={busy}
            onClick={() => void revoke()}
          >
            {busy ? "Revoking…" : "Revoke"}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Inviting somebody, with the role explained rather than merely named.
 *
 * The picker is a list of cards, not a `<select>`. Choosing a role is choosing
 * what another person may do to your audience, and a dropdown reading
 * "developer / marketing / support" asks the person picking to already know the
 * difference — which is exactly the knowledge they are missing at that moment.
 */
function InviteForm({
  granterRole,
  onCreated,
}: {
  granterRole: Role | null;
  onCreated: (invitation: CreatedInvitation) => void;
}) {
  const options = grantableRoles(granterRole);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("marketing");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = roleInfo(role);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const created = await api.post<CreatedInvitation>("/v1/organization/invitations", {
        email: email.trim(),
        role,
      });
      setEmail("");
      onCreated(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oc-panel oc-starfield" style={{ padding: 20 }}>
      <h2 className="oc-headline-md" style={{ marginTop: 0 }}>
        Invite someone
      </h2>

      <form className="stack" onSubmit={(event) => void submit(event)}>
        <div className="login__field" style={{ maxWidth: 420 }}>
          <label className="oc-label" htmlFor="invite-email">
            Email address
          </label>
          <input
            id="invite-email"
            className="oc-input"
            type="email"
            required
            autoComplete="off"
            placeholder="colleague@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "invite-error" : undefined}
          />
          <span className="oc-body-sm oc-text-muted-aa">
            The invitation is tied to this address. Signing in as anyone else and following the
            link is refused.
          </span>
        </div>

        <fieldset className="role-picker">
          <legend className="oc-label">Role</legend>
          {options.map((info) => (
            <label
              key={info.role}
              className="role-picker__option"
              aria-selected={role === info.role}
            >
              <input
                type="radio"
                name="invite-role"
                value={info.role}
                checked={role === info.role}
                onChange={() => setRole(info.role)}
              />
              <span>
                <span className="oc-body-sm" style={{ fontWeight: 500 }}>
                  {info.label}
                </span>
                <span className="oc-body-sm oc-text-secondary"> — {info.summary}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {/* The consequences of the current choice, always visible. Putting them
            behind a hover or an info icon hides them from exactly the person
            who has not made this choice before. */}
        <div className="oc-panel role-picker__detail">
          <RoleDetail info={selected} />
        </div>

        {error && (
          <div className="field-error" id="invite-error" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="oc-btn oc-btn--primary"
          disabled={busy || email.trim() === ""}
        >
          {busy ? "Creating…" : `Invite as ${selected.label.toLowerCase()}`}
        </button>
      </form>
    </div>
  );
}

/**
 * The link, shown once.
 *
 * Same contract as a new API key: the server keeps a hash, so this is the only
 * moment it exists. The panel says so plainly and does not disappear on its own
 * — a toast that fades would take the credential with it.
 */
function InviteLink({
  invitation,
  onDismiss,
}: {
  invitation: CreatedInvitation;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.accept_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in some contexts. The link is on screen and
      // selectable, so this is a missing convenience rather than a failure.
      setCopied(false);
    }
  };

  return (
    <div className="oc-panel invite-link" style={{ padding: 20 }}>
      <div className="row row--between">
        <h2 className="oc-headline-md" style={{ margin: 0 }}>
          Invitation for {invitation.email}
        </h2>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={onDismiss}>
          Dismiss
        </button>
      </div>

      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
        {invitation.emailed
          ? "This link was emailed to them. Copy it if you would rather send it yourself — it will not be shown again."
          : "Sending is not configured on this deployment, so the link was not emailed. Send it to them yourself — it will not be shown again."}
      </p>

      <div className="invite-link__row">
        <code className="oc-mono-sm invite-link__url">{invitation.accept_url}</code>
        <button type="button" className="oc-btn oc-btn--sm" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      <p className="oc-body-sm oc-text-muted-aa" style={{ marginBottom: 0 }}>
        Single use, and it expires {new Date(invitation.expires_at).toLocaleDateString()}.
      </p>
    </div>
  );
}
